import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, ChevronRight, MapPin, Briefcase, Banknote } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { BewerbungForm } from "@/components/site/BewerbungForm";
import { FlowLines } from "@/components/site/FlowLines";
import { Container, Label, Section } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import jobs from "@/data/jobs.json";

const META = {
  title: "Jobs in der Energiebranche: PV, Wärmepumpe, Speicher | energyleads",
  description:
    "Jobs in der Energiebranche: Elektriker, Elektromeister, PV-Monteure, Vertrieb und Handelsvertreter. Jetzt passende Stelle finden und ohne Bewerbungsmappe bewerben.",
};

const selectCls =
  "w-full rounded-[10px] border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white outline-none focus:border-[#3ecf9b] [&>option]:bg-[#1e2532] [&>option]:text-white";

export default function JobsIndex({ landingSlug }) {
  const landing = landingSlug ? jobs.landings[landingSlug] : null;
  const [rolle, setRolle] = useState(landing ? landing.rolle : "");
  const [bundesland, setBundesland] = useState("");

  useEffect(() => {
    document.title = landing ? landing.metaTitle : META.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", landing ? landing.metaDescription : META.description);
    window.scrollTo(0, 0);
  }, [landing]);

  const liste = useMemo(
    () =>
      Object.entries(jobs.stellen).filter(
        ([, s]) =>
          (!rolle || s.rolle === rolle) &&
          (!bundesland || s.bundesland === bundesland || s.bundesland === "deutschlandweit"),
      ),
    [rolle, bundesland],
  );

  return (
    <>
      <Header />
      <main data-testid="jobs-main">
        <section className="has-lines relative overflow-hidden bg-[#1e2532] pb-16 pt-[150px] text-white on-dark">
          <FlowLines />
          <Container>
            <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-[13px] text-white/50">
              <a href="/" className="hover:text-white">Startseite</a>
              <ChevronRight size={13} />
              {landing ? (
                <>
                  <a href="/jobs/" className="hover:text-white">Jobs</a>
                  <ChevronRight size={13} />
                  <span className="text-white/80">{landing.rolle}</span>
                </>
              ) : (
                <span className="text-white/80">Jobs</span>
              )}
            </nav>
            <Label>Jobs &amp; Bewerbung</Label>
            <h1 className="mt-7 max-w-[900px] font-bold" data-testid="jobs-h1">
              {landing ? (
                landing.h1
              ) : (
                <>
                  Jobs in der Energiebranche:{" "}
                  <span style={{ color: "var(--accent)" }}>Photovoltaik, Wärmepumpe, Batteriespeicher</span>
                </>
              )}
            </h1>
            <p className="mt-6 max-w-[700px] text-[16.5px] leading-[1.72] text-white/72">
              {landing
                ? landing.intro
                : "Wir vermitteln Fachkräfte und Vertriebler an Unternehmen, die wir selbst kennen — vom Elektromeister bis zum Handelsvertreter. Du brauchst keine Bewerbungsmappe: Ein kurzes Gespräch reicht, damit wir wissen, was zu dir passt. Deine Anfrage behandeln wir vertraulich."}
            </p>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-[720px]">
              <label className="block">
                <span className="mb-2 block text-[13px] font-semibold text-white/60">Rolle</span>
                <select data-testid="jobs-filter-rolle" className={selectCls} value={rolle} onChange={(e) => setRolle(e.target.value)}>
                  <option value="">Alle Rollen</option>
                  {jobs.rollen.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-2 block text-[13px] font-semibold text-white/60">Bundesland</span>
                <select data-testid="jobs-filter-bundesland" className={selectCls} value={bundesland} onChange={(e) => setBundesland(e.target.value)}>
                  <option value="">Alle Bundesländer</option>
                  {jobs.bundeslaender.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Container>
        </section>

        <Section testId="jobs-liste" className="has-lines overflow-hidden bg-[#f5f6f4]">
          <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
          <Container>
            <p className="text-[13px] font-bold uppercase tracking-[0.18em] text-[#0b7d57]" data-testid="jobs-count">
              {liste.length} {liste.length === 1 ? "offene Stelle" : "offene Stellen"}
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5">
              {liste.map(([slug, s]) => (
                <a
                  key={slug}
                  href={`/jobs/${slug}/`}
                  data-testid={`job-card-${slug}`}
                  className="card-hover flex flex-col gap-5 rounded-[16px] border border-[#dfe2e0] bg-white p-7 md:flex-row md:items-center md:justify-between md:p-8"
                >
                  <div>
                    <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#0b7d57]">{s.rolle}</span>
                    <h2 className="mt-3 text-[20px] font-semibold leading-[1.3] text-[#1e2532]">{s.title}</h2>
                    <p className="mt-2 text-[15px] text-[#8a90a0]">{s.unternehmen}</p>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[14.5px] text-[#565f70]">
                      <span className="inline-flex items-center gap-2">
                        <MapPin size={15} className="text-[#0f9d6e]" /> {s.ort}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <Briefcase size={15} className="text-[#0f9d6e]" /> {s.art}
                      </span>
                      {s.gehalt && (
                        <span className="inline-flex items-center gap-2">
                          <Banknote size={15} className="text-[#0f9d6e]" /> {s.gehalt}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-2 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                    Stelle ansehen <ArrowRight size={16} />
                  </span>
                </a>
              ))}
              {liste.length === 0 && (
                <div data-testid="jobs-empty" className="rounded-[16px] border border-[#dfe2e0] bg-white p-8">
                  <p className="text-[17px] font-semibold text-[#1e2532]">Aktuell keine passende Stelle ausgeschrieben.</p>
                  <p className="mt-3 text-[15.5px] leading-[1.7] text-[#565f70]">
                    Schick uns trotzdem deine Eckdaten — viele Positionen besetzen wir, bevor sie überhaupt
                    ausgeschrieben werden.
                  </p>
                  <a
                    href="#bewerbung"
                    className="mt-5 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]"
                  >
                    <span className="link-underline">Zur Initiativbewerbung</span>
                    <ArrowRight size={15} />
                  </a>
                </div>
              )}
            </div>
          </Container>
        </Section>

        <BewerbungForm defaultRolle={landing ? landing.rolle : ""} />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
